import React from 'react';
import { useSelector } from 'react-redux';
import { getDeepaiTheme } from '../../functions';
/**
 * Small helper to render a classed (optionally text-bearing) decoration node.
 *
 * @param {string} cls - Class list.
 * @param {string} [txt] - Optional text/emoji content.
 * @returns {JSX.Element}
 */
function n(cls, txt) {
    return React.createElement("span", { className: cls, key: cls }, txt);
}
const TORII = (React.createElement("svg", { className: 'dai-torii', key: 'torii', preserveAspectRatio: 'xMidYMax meet', viewBox: '0 0 120 150' },
    React.createElement("g", { fill: '#c8442e' },
        React.createElement("path", { d: 'M2 20 Q60 6 118 20 L118 31 Q60 21 2 31 Z' }),
        React.createElement("rect", { height: '8', width: '96', x: '12', y: '34' }),
        React.createElement("rect", { height: '8', width: '72', x: '24', y: '58' }),
        React.createElement("rect", { height: '112', width: '12', x: '31', y: '34' }),
        React.createElement("rect", { height: '112', width: '12', x: '77', y: '34' }))));
/**
 * Per-theme decoration markup. Positioning/appearance/animation live in the
 * theme stylesheet (constants.ts), scoped under `.deepai-stage-deco`.
 *
 * @param {string} theme - Active theme id.
 * @returns {ReactNode}
 */
function deco(theme) {
    switch (theme) {
        case '':
            return [n('dai-orb a'), n('dai-orb b')];
        case 'cool':
            return [n('dai-orb a'), n('dai-orb b'), n('dai-orb c')];
        case 'minimal':
            return [n('dai-mring'), n('dai-msq'), n('dai-marc'), n('dai-hair l')];
        case 'lux':
            return [n('dai-frameline'), n('dai-mono', 'AI'), n('dai-dust d1'), n('dai-dust d2'), n('dai-dust d3')];
        case 'natural':
            return [n('dai-float f1', '🍃'), n('dai-float f2', '🌿'), n('dai-float f3', '🍃')];
        case 'pop':
            return [n('dai-dotp p1'), n('dai-dotp p2'), n('dai-dotp p3'), n('dai-dotp p4')];
        case 'cute':
            return [n('dai-blob b1'), n('dai-blob b2'), n('dai-float f1', '💗'), n('dai-float f2', '🫧')];
        case 'clay':
            return [n('dai-puff p1'), n('dai-puff p2')];
        case 'mono':
            return [n('dai-mono-big', 'AI'), n('dai-ring r1'), n('dai-ring r2'), n('dai-tick'), n('dai-vline')];
        case 'neon':
            return [n('dai-scanline'), n('dai-brk tl'), n('dai-brk tr'), n('dai-brk bl'), n('dai-brk br'),
                n('dai-dl d1'), n('dai-dl d2'), n('dai-dl d3')];
        case 'brutal':
            return [n('dai-rule top'), n('dai-rule bot'), n('dai-block'), n('dai-outline')];
        case 'wa':
            return [TORII, n('dai-hinomaru'), n('dai-sen'), n('dai-kanji', '対話')];
        case 'crt':
            return [n('dai-term t1', '> DEEPAI//SESSION\n> STATUS: ONLINE\n> AI: LISTENING_'),
                n('dai-term t2', 'FPS 60\nLAT 42ms')];
        case 'water':
            return [n('dai-bub w1'), n('dai-bub w2'), n('dai-bub w3'), n('dai-ripple')];
        case 'ocean':
            return [n('dai-ray'), n('dai-bub w1'), n('dai-bub w2'), n('dai-bub w3')];
        default:
            return null;
    }
}
/**
 * Theme-flavoured decoration behind the AI stage video. Rendered inside
 * #largeVideoContainer (behind the centered square video) so the side areas
 * are no longer bleak. Shown only when a DeepAI theme is active.
 *
 * @returns {JSX.Element | null}
 */
export default function DeepaiStageDeco() {
    const theme = useSelector(getDeepaiTheme);
    if (!theme) {
        return null;
    }
    return React.createElement("div", { className: 'deepai-stage-deco' }, deco(theme));
}
